import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { customization, i18next, dialogs, utils } from '@yola/ws-sdk';
import { HSLColor } from '@yola/yola-palette-generator';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import websiteDesign from 'src/js/modules/website-design';
import textDecorationAttributes from '../constants/text-decoration-attributes';
import textDecorationOptions from '../constants/text-decoration-options';
import useTextDecorations from '../hooks/use-text-decorations';
import TextDecorationSettingsDialog from '../components/text-decoration-settings-dialog';
import trackers from '../trackers';

const { trackTextDecorationSettingsDialogCanceled, trackTextDecorationSettingsDialogSubmitted } =
  trackers;

const { Paragraph, Icon } = designSystem;
const {
  helpers: { parseHslColorString, parseHslaColorString },
} = websiteDesign;

const getAttributesValues = (element) =>
  Object.values(textDecorationAttributes).reduce(
    (acc, attr) => ({
      ...acc,
      [attr]: element.getAttribute(attr),
    }),
    {}
  );

const setElementsAttribute = (elements, attr, value) => {
  elements.forEach((element) => {
    element.setAttribute(attr, value);
  });
};

const removeElementsAttribute = (elements, attr) => {
  elements.forEach((element) => {
    element.removeAttribute(attr);
  });
};

const normalizeHslaColorString = (color) =>
  new HSLColor(...Object.values(parseHslaColorString(color))).toCSSValue();

const recreateHslaColorString = (color, opacity) =>
  new HSLColor(
    ...Object.values({
      ...parseHslColorString(color),
      a: opacity,
    })
  ).toCSSValue();

const getTextDecorationSettings = ({ elements, values, colors, onUpdateValue, onColorAdd }) => {
  const colorValue = values[textDecorationOptions.COLOR];
  const thicknessValue = values[textDecorationOptions.THICKNESS];
  const borderRadiusValue = values[textDecorationOptions.BORDER_RADIUS];
  const paddingVerticalValue = values[textDecorationOptions.PADDING_VERTICAL];
  const paddingHorizontalValue = values[textDecorationOptions.PADDING_HORIZONTAL];

  const createToggleSectionChangeHandler = (items) => (collapsed) =>
    items.forEach(([option, attr]) =>
      !collapsed && values[option]
        ? setElementsAttribute(
            elements,
            attr,
            Array.isArray(values[option]) ? values[option].join(' ') : values[option]
          )
        : removeElementsAttribute(elements, attr)
    );

  const createSliderChangeHandler = (option, attr, index) => (value) => {
    if (!utils.isNumeric(Number(value))) {
      return;
    }

    const currentValue = Number.isInteger(index)
      ? values[option].map((v, i) => (index === i ? value : v))
      : [value];

    setElementsAttribute(elements, attr, currentValue.join(' '));
    onUpdateValue(option, currentValue);
  };

  const createSliderClickHandler = (option, attr, multipleValueLength) => (collapsed) => {
    const optionValue = values[option];
    if (collapsed) {
      const allValuesEqual = optionValue.every((value) => value === optionValue[0]);

      if (allValuesEqual) {
        setElementsAttribute(elements, attr, optionValue[0]);
        onUpdateValue(option, [optionValue[0]]);
      }
    } else {
      const multipleValue =
        optionValue.length === 1
          ? [...Array(multipleValueLength).keys()].map(() => optionValue[0])
          : optionValue;

      setElementsAttribute(elements, attr, multipleValue.join(' '));
      onUpdateValue(option, multipleValue);
    }
  };

  return {
    [textDecorationOptions.COLOR]: {
      sectionProps: {
        title: i18next.t('Color'),
        defaultValue: !elements[0].hasAttribute(textDecorationAttributes.COLOR),
        onChange: createToggleSectionChangeHandler([
          [textDecorationOptions.COLOR, textDecorationAttributes.COLOR],
        ]),
      },
      controls: [
        {
          type: 'color-selector',
          selectedItemId: colorValue,
          items: colors,
          onAddButtonClick: onColorAdd,
          onClick: ({ color }) => {
            setElementsAttribute(elements, textDecorationAttributes.COLOR, color);
            onUpdateValue(textDecorationOptions.COLOR, color);
          },
        },
      ],
    },
    [textDecorationOptions.THICKNESS]: {
      sectionProps: {
        title: i18next.t('Thickness'),
        defaultValue: !elements[0].hasAttribute(textDecorationAttributes.THICKNESS),
        onChange: createToggleSectionChangeHandler([
          [textDecorationOptions.THICKNESS, textDecorationAttributes.THICKNESS],
        ]),
      },
      controls: [
        {
          type: 'slider',
          iconGlyph: 'border-thickness',
          value: Number(thicknessValue),
          min: elements[0].thickness.minLevel,
          max: elements[0].thickness.maxLevel,
          step: 1,
          onChange: (value) => {
            setElementsAttribute(elements, textDecorationAttributes.THICKNESS, value);
            onUpdateValue(textDecorationOptions.THICKNESS, value);
          },
        },
      ],
    },
    [textDecorationOptions.BORDER_RADIUS]: {
      sectionProps: {
        title: i18next.t('Corners'),
        popoverProps: {
          imgSrc: '/images/text-decorations/label-corners.gif',
          content: (
            <Paragraph appearance="white" size="small">
              {i18next.t('Adjust corners for label')}.<br />
              {i18next.t('Click the {icon}', { icon: '' })}&nbsp;
              <Icon type="white" size={16} glyph="corner-radius" insideText />
              &nbsp;
              {i18next.t('{click_the_icon} to adjust each one separately', { click_the_icon: '' })}
            </Paragraph>
          ),
        },
        defaultValue: !elements[0].hasAttribute(textDecorationAttributes.BORDER_RADIUS),
        onChange: createToggleSectionChangeHandler([
          [textDecorationOptions.BORDER_RADIUS, textDecorationAttributes.BORDER_RADIUS],
        ]),
      },
      controls: [
        {
          type: 'slider',
          title: i18next.t('Individual corners'),
          mixedLabel: i18next.t('Mixed'),
          iconGlyph: 'corner-radius',
          value:
            borderRadiusValue.length === 1
              ? Number(borderRadiusValue[0])
              : Math.max(...borderRadiusValue),
          min: elements[0].borderRadius.minLevel,
          max: elements[0].borderRadius.maxLevel,
          step: 1,
          items: [
            [0, 'corner-top-left-radius'],
            [3, 'corner-bottom-left-radius'],
            [1, 'corner-top-right-radius'],
            [2, 'corner-bottom-right-radius'],
          ].map(([index, iconGlyph]) => ({
            iconGlyph,
            value: Number(
              borderRadiusValue.length > 1 ? borderRadiusValue[index] : borderRadiusValue[0]
            ),
            onChange: createSliderChangeHandler(
              textDecorationOptions.BORDER_RADIUS,
              textDecorationAttributes.BORDER_RADIUS,
              index
            ),
          })),
          indeterminate: borderRadiusValue.length > 1,
          onChange: createSliderChangeHandler(
            textDecorationOptions.BORDER_RADIUS,
            textDecorationAttributes.BORDER_RADIUS
          ),
          onClick: createSliderClickHandler(
            textDecorationOptions.BORDER_RADIUS,
            textDecorationAttributes.BORDER_RADIUS,
            4
          ),
        },
      ],
    },
    [textDecorationOptions.PADDING]: {
      sectionProps: {
        title: i18next.t('Spacing'),
        defaultValue:
          !elements[0].hasAttribute(textDecorationAttributes.PADDING_VERTICAL) ||
          !elements[0].hasAttribute(textDecorationAttributes.PADDING_HORIZONTAL),
        onChange: createToggleSectionChangeHandler([
          [textDecorationOptions.PADDING_VERTICAL, textDecorationAttributes.PADDING_VERTICAL],
          [textDecorationOptions.PADDING_HORIZONTAL, textDecorationAttributes.PADDING_HORIZONTAL],
        ]),
      },
      controls: [
        {
          type: 'slider',
          title: i18next.t('Top&Bottom'),
          mixedLabel: i18next.t('Mixed'),
          iconGlyph: 'padding-vertical',
          popoverProps: {
            imgSrc: '/images/text-decorations/label-vertical-padding.gif',
            content: (
              <Paragraph appearance="white" size="small">
                {i18next.t('Adjust the top and bottom spaces inside text effect')}.&nbsp;
                {i18next.t('Click the {icon}', { icon: '' })}&nbsp;
                <Icon type="white" size={16} glyph="padding-vertical" insideText />
                &nbsp;
                {i18next.t('{click_the_icon} to adjust each one separately', {
                  click_the_icon: '',
                })}
              </Paragraph>
            ),
          },
          value:
            paddingVerticalValue.length === 1
              ? Number(paddingVerticalValue[0])
              : Math.max(...paddingVerticalValue),
          min: elements[0].padding.vertical.minValue,
          max: elements[0].padding.vertical.maxValue,
          step: 1,
          items: [
            [0, 'padding-top'],
            [1, 'padding-bottom'],
          ].map(([index, iconGlyph]) => ({
            iconGlyph,
            value: Number(
              paddingVerticalValue.length > 1
                ? paddingVerticalValue[index]
                : paddingVerticalValue[0]
            ),
            onChange: createSliderChangeHandler(
              textDecorationOptions.PADDING_VERTICAL,
              textDecorationAttributes.PADDING_VERTICAL,
              index
            ),
          })),
          indeterminate: paddingVerticalValue.length > 1,
          onChange: createSliderChangeHandler(
            textDecorationOptions.PADDING_VERTICAL,
            textDecorationAttributes.PADDING_VERTICAL
          ),
          onClick: createSliderClickHandler(
            textDecorationOptions.PADDING_VERTICAL,
            textDecorationAttributes.PADDING_VERTICAL,
            2
          ),
        },
        {
          type: 'slider',
          title: i18next.t('Left&Right'),
          mixedLabel: i18next.t('Mixed'),
          iconGlyph: 'padding-horizontal',
          popoverProps: {
            imgSrc: '/images/text-decorations/label-horizontal-padding.gif',
            content: (
              <Paragraph appearance="white" size="small">
                {i18next.t('Adjust the left and right spaces inside text effect')}.&nbsp;
                {i18next.t('Click the {icon}', { icon: '' })}&nbsp;
                <Icon type="white" size={16} glyph="padding-horizontal" insideText />
                &nbsp;
                {i18next.t('{click_the_icon} to adjust each one separately', {
                  click_the_icon: '',
                })}
              </Paragraph>
            ),
          },
          value:
            paddingHorizontalValue.length === 1
              ? Number(paddingHorizontalValue[0])
              : Math.max(...paddingHorizontalValue),
          min: elements[0].padding.horizontal.minValue,
          max: elements[0].padding.horizontal.maxValue,
          step: 1,
          items: [
            [0, 'padding-left'],
            [1, 'padding-right'],
          ].map(([index, iconGlyph]) => ({
            iconGlyph,
            value: Number(
              paddingHorizontalValue.length > 1
                ? paddingHorizontalValue[index]
                : paddingHorizontalValue[0]
            ),
            onChange: createSliderChangeHandler(
              textDecorationOptions.PADDING_HORIZONTAL,
              textDecorationAttributes.PADDING_HORIZONTAL,
              index
            ),
          })),
          indeterminate: paddingHorizontalValue.length > 1,
          onChange: createSliderChangeHandler(
            textDecorationOptions.PADDING_HORIZONTAL,
            textDecorationAttributes.PADDING_HORIZONTAL
          ),
          onClick: createSliderClickHandler(
            textDecorationOptions.PADDING_HORIZONTAL,
            textDecorationAttributes.PADDING_HORIZONTAL,
            2
          ),
        },
      ],
    },
  };
};

const getCaptions = () => ({
  title: i18next.t('Text effects'),
  changeDecorationButton: i18next.t('Change effect'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
});

const TextDecorationSettingsContainer = ({
  elements,
  wrapperElement,
  premiumAvailable,
  getSharedData,
  resolveSharedData,
  onSubmit: onSubmitProp = null,
  onCancel: onCancelProp = null,
}) => {
  const captions = getCaptions();
  const {
    initialDecorationType = elements[0].type,
    initialDialogPosition = null,
    initialWrapperElement = wrapperElement,
    initialAttributesValues = getAttributesValues(elements[0]),
    onSubmit = onSubmitProp,
    onCancel = onCancelProp,
  } = getSharedData();

  const initialValues = useRef({
    [textDecorationOptions.COLOR]: normalizeHslaColorString(elements[0].color.value),
    [textDecorationOptions.THICKNESS]: elements[0].thickness.level,
    [textDecorationOptions.BORDER_RADIUS]: elements[0].borderRadius.level,
    [textDecorationOptions.PADDING_VERTICAL]: elements[0].padding.vertical.level,
    [textDecorationOptions.PADDING_HORIZONTAL]: elements[0].padding.horizontal.level,
  });

  const attributesValues = useRef(initialAttributesValues);

  const [dialogPosition, setDialogPosition] = useState(initialDialogPosition);
  const [values, setValues] = useState({
    ...initialValues.current,
    [textDecorationOptions.BORDER_RADIUS]:
      initialValues.current[textDecorationOptions.BORDER_RADIUS].split(' '),
    [textDecorationOptions.PADDING_VERTICAL]:
      initialValues.current[textDecorationOptions.PADDING_VERTICAL].split(' '),
    [textDecorationOptions.PADDING_HORIZONTAL]:
      initialValues.current[textDecorationOptions.PADDING_HORIZONTAL].split(' '),
  });

  const { getDecorationById } = useTextDecorations();
  const customColors = useSelector(customization.selectors.getCustomColors);

  const decoration = getDecorationById(elements[0].type);
  const defaultColor = normalizeHslaColorString(elements[0].color.default);
  const colors = Array.from(
    new Set([
      initialValues.current[textDecorationOptions.COLOR],
      ...customColors.map(({ value, opacity }) => recreateHslaColorString(value, opacity)),
      defaultColor,
    ])
  ).map((color) => ({
    id: color,
    color,
  }));

  const saveSharedData = () => {
    resolveSharedData({
      ...getSharedData(),
      initialDecorationType,
      initialDialogPosition: dialogPosition,
      initialWrapperElement,
      initialAttributesValues: attributesValues.current,
      onSubmit,
      onCancel,
    });
  };

  const handleUpdateValue = (optionName, value) =>
    setValues({
      ...values,
      [optionName]: value,
    });

  const handleColorAdd = () => {
    saveSharedData();
    dialogs.operations.show(dialogTypes.TEXT_DECORATION_ADD_COLOR_DIALOG, {
      elements,
      premiumAvailable,
    });
  };

  const handleDecorationChange = () => {
    saveSharedData();
    dialogs.operations.show(dialogTypes.TEXT_DECORATION_LIBRARY_DIALOG, {
      initialDecorationType,
      initialDialogPosition: dialogPosition,
      elements,
      premiumAvailable,
    });
  };

  const handleSumbit = () => {
    const attributes = {};

    Object.values(textDecorationAttributes).forEach((attrName) => {
      if (attrName === textDecorationAttributes.TYPE) {
        attributes[attrName] = elements[0].type;
        return;
      }
      const attrValue = elements[0].getAttribute(attrName);

      if (attrValue) {
        attributes[attrName] = attrValue;
      }
    });

    trackTextDecorationSettingsDialogSubmitted({
      targetElement: initialWrapperElement,
      initialAttributesValues: attributesValues.current,
      currentAttributesValues: getAttributesValues(elements[0]),
      premiumAvailable,
      textDecorationPresetSelected: decoration.id,
    });

    onSubmit(attributes);
    dialogs.operations.hide();
  };

  const handleCancel = () => {
    trackTextDecorationSettingsDialogCanceled({ targetElement: initialWrapperElement });
    onCancel();
    dialogs.operations.hide();
  };

  const handlePositioningReady = (position) => {
    if (initialDialogPosition) {
      return;
    }

    setDialogPosition(position);
  };

  const handleDialogDragEnd = (event, position) => {
    setDialogPosition(position);
  };

  const allDecorationSettings = getTextDecorationSettings({
    elements,
    values,
    colors,
    onUpdateValue: handleUpdateValue,
    onColorAdd: handleColorAdd,
  });

  const decorationSettings = decoration.options.map(
    (optionName) => allDecorationSettings[optionName]
  );

  return (
    <TextDecorationSettingsDialog
      decorationPreviewUrl={decoration.previewUrl}
      decorationSettings={decorationSettings}
      dialogPosition={initialDialogPosition}
      captions={captions}
      elementForPositioning={elements[0]}
      onDecorationChange={handleDecorationChange}
      onSubmit={handleSumbit}
      onCancel={handleCancel}
      onPositioningReady={handlePositioningReady}
      onOverlayClick={handleSumbit}
      onDialogDragEnd={handleDialogDragEnd}
    />
  );
};

TextDecorationSettingsContainer.propTypes = {
  elements: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape(), PropTypes.node])).isRequired,
  wrapperElement: PropTypes.node.isRequired,
  premiumAvailable: PropTypes.bool.isRequired,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

export default TextDecorationSettingsContainer;
